import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Dimensions,
  Image,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  ScrollView,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View
} from 'react-native'
import * as Progress from 'react-native-progress'
import Swiper from 'react-native-swiper'

import { Text } from '../../components'
import { DS, DSIcon } from '../../design-system'
import XSvg from '../../design-system/Icons/X.svg'
import { translate } from '../../i18n'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { color } from '../../theme'
import { startStyles } from './styles'
import { Href, Route, useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router'
import { defaultBreakPoints, isWeb, WebWrapper } from '@/components/WebWrapper'
import { StyleSheet } from 'react-native'
import { LoaderSpinner } from '@/components/LoaderSpinner'
import { useInitialLinkingStore } from '@/hooks/useInitialLinkingStore'
import { onboardingQuestionsToFilterById } from '@/models/session/session-user'
import { useMutation } from '@tanstack/react-query'

interface QuestionModal {
  id: number
  subtitle: string
  question: string
  values: string[]
  type: string
  answers: string[]
  footer?: string
}

const eventsToTrackById = {
  1: events.onboarding.pronoun_form_completed,
  2: events.onboarding.skills_form_completed,
  3: events.onboarding.referral_form_completed,
  4: events.onboarding.reason_form_completed,
  5: events.onboarding.age_form_completed
}

const eventsProperty = {
  1: 'pronoun',
  2: 'skill_level',
  3: 'referral',
  4: 'reason',
  5: 'age_range'
}

const nameObj = {
  id: 0,
  subtitle: translate('memberForm.pokerNamesubtitle'),
  question: translate('memberForm.pokerNameQuestion'),
  values: [],
  type: 'single',
  answers: []
}

export const UserInfoScreen = observer(() => {
  const router = useRouter()
  const inputRef = useRef(null)
  const windowSize = useWindowDimensions()

  const store = useStore()
  const [userName, setUserName] = useState<string>('')
  const [questionAnswers, setQuestionAnswers] = useState<QuestionModal[]>([nameObj])
  const [isLoading, setIsLoading] = useState(false)
  const [showOtherInput, setShowOtherInput] = useState(false)
  const [otherResponses, setOtherResponses] = useState<string[]>([])
  const [minResumingStep, setMinResumingStep] = useState(0)
  const [swiperIndex, setSwiperIndex] = useState(0)
  const [isNextActive, setIsNext] = useState(false)
  const swiperRef = useRef<Swiper>(null)

  const { isResuming } = useLocalSearchParams<{ isResuming: string }>()

  const scrollViewRef = useRef<ScrollView>(null)
  const isFirstStep = swiperIndex === 0
  const isLastStep = swiperIndex >= questionAnswers.length - 1

  const currentItem =
    swiperIndex < questionAnswers.length ? questionAnswers[swiperIndex] : questionAnswers[questionAnswers.length - 1]

  const isOptionSelected = useCallback(
    (option: string) => {
      return currentItem?.answers?.includes(option)
    },
    [currentItem.answers]
  )

  /**
   * Merge old and new questions
   * @param oldQuestions
   * @param newQuestions
   * @returns
   * @description This function is used to merge the questions that are already answered by the user and the new questions
   * that are being added to the onboarding process. We do this to ensure that the user's answers are not lost when new questions are
   * saved with empty answers.
   **/
  const mergeOldAndNewQuestions = (oldQuestions: QuestionModal[], newQuestions: QuestionModal[]) => {
    const missingQuestions = newQuestions?.filter((newQuestion) => {
      return !oldQuestions.some((oldQuestion) => oldQuestion.id === newQuestion.id)
    })
    return [...oldQuestions, ...missingQuestions]
  }

  const cantGoBack = isResuming && swiperIndex <= minResumingStep

  const markTaskAsCompleted = async () => {
    try {
      console.log('userinfo screen markTaskAsCompleted 1')
      if (store.session.selectedDailyTask) await store.session.updateDailyTaskStatus()
      console.log('userinfo screen markTaskAsCompleted 1')
    } catch (error) {
      console.log('Error in markTaskAsCompleted', error)
    }
  }

  const buttonText = () => {
    const isLastStep = swiperIndex + 1 === questionAnswers.length
    const isFirstStep = swiperIndex === 0
    if (
      isFirstStep &&
      store.session.selectedDailyTask &&
      store.session.selectedDailyTask.taskName.toLowerCase() === 'complete your profile'
    ) {
      return 'Next'
    }
    if (
      isLastStep &&
      store.session.selectedDailyTask &&
      store.session.selectedDailyTask.taskName.toLowerCase() === 'complete your profile'
    ) {
      return 'Done'
    }
    if (isLastStep && !isResuming) {
      return 'Create Account'
    }
    return 'Next'
  }

  const isCTADisabled = () => {
    if (isFirstStep && userName && (userName.length < 1 || userName.length > 15)) {
      return true
    }
    if (!isNextActive) {
      return true
    }
    false
  }

  const resumeFromStep = useCallback(
    (startFromStep: number) => {
      if (isResuming) {
        // if user is resuming, we want to start from the next step
        setMinResumingStep(startFromStep + 1)
        setSwiperIndex(startFromStep + 1)
      } else {
        setSwiperIndex(startFromStep)
      }
    },
    [isResuming]
  )

  const onSelect = (question: QuestionModal, answer: string) => {
    const index = questionAnswers.findIndex((item) => question.question === item.question)
    const questionObj = questionAnswers[index]
    if (question.type === 'single') {
      singleSelect(questionObj, answer)
    } else {
      multiSelect(questionObj, answer)
    }

    questionAnswers[index] = questionObj
    setQuestionAnswers([...questionAnswers])
  }

  const singleSelect = (question: QuestionModal, answer: string) => {
    if (answer === 'Other') {
      setShowOtherInput(true)
    } else {
      setShowOtherInput(false)
      updateOtherResponse('')
    }
    question.answers = [answer]
  }

  const multiSelect = (question: QuestionModal, answer: string) => {
    const answerIndex = question.answers.findIndex((item) => item === answer)
    const alreadySelected = answerIndex !== -1

    if (alreadySelected) {
      if (answer === 'Other') {
        setShowOtherInput(false)
        updateOtherResponse('')
      }
      question.answers.splice(answerIndex, 1)
    } else {
      if (answer === 'Other') {
        setShowOtherInput(true)
      }
      question.answers.push(answer)
    }
  }

  const updateOtherResponse = (value: string) => {
    const updatedOther = [...otherResponses]
    updatedOther[swiperIndex] = value
    setOtherResponses(updatedOther)
  }

  const onBack = () => {
    if (swiperIndex <= 0) {
      if (
        store.session.selectedDailyTask &&
        store.session.selectedDailyTask.taskName.toLowerCase() === 'complete your profile'
      ) {
        router.navigate('/(root)/(tabs)')
      } else {
        router.replace('/onboarding/where-are-you')
      }
    } else {
      swiperRef?.current?.scrollBy(-1, true)
      setSwiperIndex(swiperIndex - 1)
    }
  }

  const onClose = () => {
    router.dismissTo('/(root)/profile' as Route)
  }

  const trackEvent = (question: QuestionModal, answer: string[]) => {
    const noEventFound = !Object.prototype.hasOwnProperty.call(
      eventsToTrackById,
      question.id as keyof typeof eventsToTrackById
    )
    if (noEventFound) {
      return
    }
    const eventToTrack = eventsToTrackById[question.id as keyof typeof eventsToTrackById]
    const isMultiple = question.type === 'multiple'
    const parsedAnswers = isMultiple ? answer.join(', ') : answer[0]

    const eventsPropertyKey = eventsProperty[question.id as keyof typeof eventsProperty]
    store.analytics.setEvent(eventToTrack, {
      segmentUserId: store.analytics.getSegmentUserID(),
      segmentIdentity: store.analytics.getUserIdentify(),
      [eventsPropertyKey]: parsedAnswers
    })

    // TODO see if its necessary to set the user identify for the skills form
    const user = store.session.user
    if (eventToTrack === events.onboarding.skills_form_completed && user?.email) {
      store.analytics.setUserIdentify(user._id, {
        name: user.name,
        email: user?.email,
        skill_level: parsedAnswers
      })
    }
  }

  const finishOnboarding = () => {
    if (
      !isResuming //&&
      //store.session.selectedDailyTask &&
      //store.session.selectedDailyTask.taskName.toLowerCase() !== 'complete your profile'
    ) {
      router.navigate('/onboarding/create-avatar')
    } else {
      //markTaskAsCompleted()

      const initialUrl = useInitialLinkingStore.getState().url
      if (initialUrl?.pathname) {
        useInitialLinkingStore.getState().dispose('url')
        const fullHref = initialUrl.search
          ? `${initialUrl.pathname}${initialUrl.search}`
          : initialUrl.pathname
        store.analytics.setEvent('Deep Link Consumed', { url: fullHref, source: 'UserInfo' })
        router.replace(fullHref as Href)
        return
      }
      router.navigate('/(root)/(tabs)')
    }
  }

  const onNextMutation = useMutation({
    mutationFn: async () => {
      Keyboard.dismiss()

      if (isFirstStep) {
        if (userName) {
          const isUsernameTaken = await store.session.checkIsUsernameTaken(userName)
          if (isUsernameTaken) {
            throw new Error(`The username ${userName} is already taken`)
          }
          await store.session.updateUserNameOnAppAndWordpress(userName)
          await store.session.updateUserNameAndAnalytics(userName)
          swiperRef?.current?.scrollBy(1, true)
          setSwiperIndex(swiperIndex + 1)
        }
      } else {
        const newQuestionAnswers = [...questionAnswers]
        const question = newQuestionAnswers[swiperIndex]
        let answers: string[] = []
        if (isLastStep) {
          const updatedAnswers = newQuestionAnswers
            .filter((obj) => obj.id !== 0)
            .map((question, index) => {
              // index + 1 because the first question is the name question
              const otherResponse = otherResponses[index + 1]
              answers = otherResponse ? [...question.answers, otherResponse] : question.answers
              return {
                ...question,
                answers
              }
            })
          const payload = {
            isUserNew: true,
            questionAnswers: updatedAnswers,
            isUserFromLiveEvent: false
          }
          // do this on change name screen too
          await store.session.updateUserInfo(payload)
          await store.session.getUserInfo()
          await store.session.fetchOnboardingProgress()
          finishOnboarding()
        } else {
          const otherResponse = otherResponses[swiperIndex]
          answers = otherResponse ? [...question.answers, otherResponse] : question.answers
          await store.session.updateUserAnswers(question.id, answers)
          await store.session.getUserInfo()
          swiperRef?.current?.scrollBy(1, true)
          setSwiperIndex(swiperIndex + 1)
        }
        setQuestionAnswers(newQuestionAnswers)
        trackEvent(questionAnswers[swiperIndex], answers)
      }
    }
  })

  const Header = () => {
    return (
      <View style={styles.webContainer}>
        <View style={startStyles.header}>
          {!cantGoBack && (
            <TouchableOpacity style={{ position: 'absolute', left: 24 }} onPress={onBack}>
              <Image
                style={{ height: 24, width: 24 }}
                source={require('../../../assets/userInfo/back.png')}
                resizeMode="contain"
              />
            </TouchableOpacity>
          )}
          {!isResuming && (
            <Text style={startStyles.headerTitle} numberOfLines={1}>
              {swiperIndex + 1} of {questionAnswers.length}
            </Text>
          )}
          {cantGoBack && (
            <TouchableOpacity style={{ position: 'absolute', right: 24 }} onPress={onClose}>
              <XSvg width={24} height={24} color={'#000'} />
            </TouchableOpacity>
          )}
        </View>
      </View>
    )
  }

  const ProgressBar = useCallback(() => {
    const progressValue = swiperIndex / (questionAnswers.length - 1)

    if (!(progressValue >= 0 && progressValue <= 1)) {
      return null
    }
    return (
      <Progress.Bar
        borderWidth={0}
        borderRadius={0}
        borderColor="gray"
        progress={progressValue}
        animated={true}
        unfilledColor="lightgray"
        color="#000"
        width={windowSize.width}
        height={2}
      />
    )
  }, [swiperIndex, questionAnswers, windowSize])

  useFocusEffect(
    useCallback(() => {
      setIsLoading(true)
      const fetchQuestions = async () => {
        //  TODO: create a resolver for this and fetch just once
        const progressResponse = await store.session.fetchOnboardingProgress()
        const questionsResponse = await store.session.fetchOnboardingQuestions()
        const userInfo = await store.session.getUserInfo()
        const userQuestionsWithAnswers = (userInfo?.questionAnswers as QuestionModal[]) || []
        const userQuestionsWithAnswersFiltered = userQuestionsWithAnswers.filter(
          (q) => !onboardingQuestionsToFilterById.includes(q.id)
        )

        const allQuestions = questionsResponse?.questions || []
        const mappedQuestions: QuestionModal[] = allQuestions.map((item) => {
          const values: string[] = item.values ? [...item.values] : []

          return {
            id: item.id || 0,
            subtitle: item.subtitle || '',
            question: item.question || '',
            values,
            type: item.type || 'single',
            answers: []
          }
        })
        const allQuestionsWithAnswers = mergeOldAndNewQuestions(userQuestionsWithAnswersFiltered, mappedQuestions)
        const nameAndAllQuestions = [nameObj, ...allQuestionsWithAnswers]

        setQuestionAnswers(nameAndAllQuestions)
        resumeFromStep(progressResponse.answeredQuestions || 0)
        setIsLoading(false)
      }
      fetchQuestions()
    }, [store.session, resumeFromStep])
  )

  useEffect(() => {
    if (swiperIndex > 0 && questionAnswers && questionAnswers[swiperIndex]?.answers?.length > 0) {
      setIsNext(true)
    } else if (swiperIndex === 0 && userName?.trim() !== '' && userName && userName?.length > 0) {
      setIsNext(true)
    } else {
      setIsNext(false)
    }
  }, [swiperIndex, questionAnswers, userName, store.analytics])

  useEffect(() => {
    const playerName = store.session.user?.name
    if (playerName && !playerName.includes('placeholder')) {
      setUserName(playerName)
    }
  }, [store.session.user?.name])

  useEffect(() => {
    store.analytics.setEvent(events.onboarding.onboarding_slide_viewed, {
      slideNumber: swiperIndex + 1,
      segmentUserId: store.analytics.getSegmentUserID(),
      segmentIdentity: store.analytics.getUserIdentify()
    })
  }, [swiperIndex, store.analytics])

  useEffect(() => {
    const currentOtherResponse = otherResponses[swiperIndex]
    setShowOtherInput(!!currentOtherResponse)
  }, [swiperIndex, otherResponses])

  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F6F2E9' }}>
        <StatusBar style={'dark'} />
        <ActivityIndicator size="small" color="#7B043D" />
      </View>
    )
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 50}
      style={{ flex: 1 }}
    >
      <SafeAreaView style={[startStyles.startContainer, { backgroundColor: '#F6F2E9' }]}>
        <StatusBar style={'dark'} />

        <Header />
        <ProgressBar />
        <WebWrapper>
          {swiperIndex === 0 ? (
            <View style={startStyles.firstSlide}>
              <DS.Text variant="Heading 2/Regular">{currentItem.question}</DS.Text>
              {currentItem?.subtitle && (
                <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey">
                  {currentItem?.subtitle}
                </DS.Text>
              )}
              <DS.Frame style={startStyles.nameContainer}>
                <DS.Frame row justifyContent="space-between" alignItems="center" style={{ marginTop: 32 }}>
                  <DS.Text variant="Body Medium/Medium">{translate('memberForm.name')}</DS.Text>
                  <DS.Text
                    variant="Body Small/Regular"
                    color={userName && userName.length > 15 ? 'Utility/Warning' : 'Global/Black'}
                  >
                    {userName?.length}/15
                  </DS.Text>
                </DS.Frame>
                <TextInput
                  ref={inputRef}
                  placeholder="Enter a name"
                  placeholderTextColor={'#68706D'}
                  style={[
                    startStyles.inputField,
                    { borderColor: userName && userName?.length > 15 ? color.palette.red : color.palette.black }
                  ]}
                  allowFontScaling={false}
                  autoCapitalize={'words'}
                  autoCorrect={false}
                  maxLength={15}
                  autoComplete="off"
                  value={userName || ''}
                  onChangeText={(value: string) => {
                    if (value === '') {
                      setIsNext(false)
                    } else {
                      setIsNext(true)
                    }
                    setUserName(value)
                  }}
                  autoFocus={true}
                />
                <DS.Text variant="Body Small/Regular" color={'Utility/Warning'}>
                  {onNextMutation.error?.message ?? ''}
                </DS.Text>
              </DS.Frame>
            </View>
          ) : (
            <ScrollView showsVerticalScrollIndicator={false} ref={scrollViewRef}>
              <DS.Frame style={startStyles.slide}>
                <DS.Frame>
                  <Text style={startStyles.question}>{currentItem.question}</Text>
                  {currentItem?.subtitle && (
                    <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey">
                      {currentItem?.subtitle}
                    </DS.Text>
                  )}
                  {currentItem.type === 'multiple' ? (
                    <Text style={startStyles.multipleSelect}>Select all that apply.</Text>
                  ) : null}
                </DS.Frame>
                <DS.Frame style={startStyles.questionsAndFooter}>
                  <DS.Frame style={startStyles.questionOptions}>
                    {currentItem.values.map((option: string, index: number) => {
                      const [checkedIcon, uncheckedIcon] =
                        currentItem.type === 'single' ? ['Radio', 'Radio-uncheck'] : ['Checkbox', 'Checkbox-uncheck']
                      const isSelected = isOptionSelected(option)
                      const icon = isSelected ? checkedIcon : uncheckedIcon

                      return (
                        <TouchableOpacity onPress={() => onSelect(currentItem, option)} key={`option_${index}`}>
                          <DS.Frame
                            style={[startStyles.optionView, { borderColor: isSelected ? '#000' : 'lightgray' }]}
                          >
                            <DS.Icon name={icon as DSIcon} size={16} color="Global/Black" />
                            <DS.Text style={startStyles.optionText} variant="Body Large/Regular" numberOfLines={3}>
                              {option}
                            </DS.Text>
                          </DS.Frame>
                        </TouchableOpacity>
                      )
                    })}
                    {showOtherInput ? (
                      <TextInput
                        placeholder="Please specify..."
                        placeholderTextColor={'#68706D'}
                        style={[startStyles.inputField, { borderColor: 'lightgray', paddingHorizontal: 20 }]}
                        allowFontScaling={false}
                        autoCapitalize={'words'}
                        autoCorrect={false}
                        maxLength={50}
                        autoComplete="off"
                        value={otherResponses[swiperIndex]}
                        onFocus={() => {
                          setTimeout(() => {
                            scrollViewRef.current?.scrollToEnd({ animated: true })
                          }, 100)
                        }}
                        onChangeText={(value: string) => {
                          const updatedOther = [...otherResponses]
                          updatedOther[swiperIndex] = value
                          setOtherResponses(updatedOther)
                        }}
                        autoFocus
                      />
                    ) : null}
                  </DS.Frame>
                  <DS.Frame>
                    {currentItem?.footer && (
                      <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey">
                        {currentItem?.footer}
                      </DS.Text>
                    )}
                  </DS.Frame>
                </DS.Frame>
              </DS.Frame>
            </ScrollView>
          )}

          <DS.Frame style={startStyles.buttonFrame}>
            <DS.Button
              type="On Light"
              variant="Large/Primary"
              disabled={onNextMutation.isPending || isCTADisabled()}
              onPress={() => onNextMutation.mutate()}
              rightIconContainerStyle={{
                position: 'absolute',
                right: 16
              }}
              rightIcon={onNextMutation.isPending && <LoaderSpinner />}
            >
              {buttonText()}
            </DS.Button>
          </DS.Frame>
        </WebWrapper>
      </SafeAreaView>
    </KeyboardAvoidingView>
  )
})

const styles = StyleSheet.create({
  webContainer: {
    width: '100%',
    maxWidth: isWeb ? defaultBreakPoints.sm : undefined,
    marginHorizontal: isWeb ? 'auto' : undefined
  }
})
